export const STORAGE_KEY_PREFIX = 'nexus:items-table-expansion-state:';
const MAX_INSTANCES = 150;

export type ExpansionState = { type: 'include' | 'exclude'; ids: Set<number> };

type TableExpansionEntry = {
  type: 'include' | 'exclude';
  ids: number[];
  lastAccessed: number;
};

const buildKey = (instanceKey: string): string => `${STORAGE_KEY_PREFIX}${instanceKey}`;

const isValidEntry = (value: unknown): value is TableExpansionEntry => {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Record<string, unknown>;
  if (entry.type !== 'include' && entry.type !== 'exclude') return false;
  if (!Array.isArray(entry.ids)) return false;
  if (!entry.ids.every((id) => typeof id === 'number')) return false;
  if (typeof entry.lastAccessed !== 'number') return false;
  return true;
};

export const readExpansionEntry = (instanceKey: string): ExpansionState | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(buildKey(instanceKey));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidEntry(parsed)) return null;
    try {
      const touched: TableExpansionEntry = { ...parsed, lastAccessed: Date.now() };
      window.localStorage.setItem(buildKey(instanceKey), JSON.stringify(touched));
    } catch {}
    return { type: parsed.type, ids: new Set(parsed.ids) };
  } catch {
    return null;
  }
};

export const writeExpansionEntry = (instanceKey: string, state: ExpansionState): void => {
  if (typeof window === 'undefined') return;
  try {
    const entry: TableExpansionEntry = {
      type: state.type,
      ids: [...state.ids],
      lastAccessed: Date.now(),
    };
    window.localStorage.setItem(buildKey(instanceKey), JSON.stringify(entry));
  } catch {
    // localStorage disabled or quota exceeded — silently ignore
  }
};

export const pruneExpansionEntries = (): void => {
  if (typeof window === 'undefined') return;
  try {
    const tableKeys: Array<{ key: string; lastAccessed: number }> = [];

    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith(STORAGE_KEY_PREFIX)) continue;

      try {
        const raw = window.localStorage.getItem(key);
        const parsed: unknown = raw ? JSON.parse(raw) : null;
        const lastAccessed =
          parsed && typeof parsed === 'object' && 'lastAccessed' in parsed
            ? (parsed as Record<string, unknown>).lastAccessed
            : 0;
        tableKeys.push({ key, lastAccessed: typeof lastAccessed === 'number' ? lastAccessed : 0 });
      } catch {
        tableKeys.push({ key, lastAccessed: 0 });
      }
    }

    if (tableKeys.length <= MAX_INSTANCES) return;

    tableKeys.sort((a, b) => a.lastAccessed - b.lastAccessed);
    const entriesToRemove = tableKeys.slice(0, tableKeys.length - MAX_INSTANCES);
    for (const { key } of entriesToRemove) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // localStorage disabled — silently ignore
  }
};
